import React, { useEffect, useRef, useState } from "react";

const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t);
const smooth = (x) => x * x * (3 - 2 * x);

// Five distinct supplied photographs → the narrative progression
// DESIGN → OBJECT → ELEMENTS → LAYERING → COMPLETION. The final image carries
// both the closing statement and the CTA (image holds while the CTA emerges).
const IMAGES = [
  "/narrative/01_drawing.jpg",
  "/narrative/02_furniture.jpg",
  "/narrative/03_elements.jpg",
  "/narrative/04_layering.jpg",
  "/narrative/05_completion.jpg",
];
const LEFT_SCRIM =
  "linear-gradient(90deg, rgba(18,17,16,0.78) 0%, rgba(18,17,16,0.52) 30%, rgba(24,21,18,0.22) 58%, rgba(24,21,18,0) 82%)";

/**
 * "Narrative Pause" — cinematic image + text crossfade.
 *
 * A single pinned viewport (sticky) with a tall scroll runway. As the user
 * scrolls, the image AND its text crossfade together as one narrative state:
 * drawing → furniture → elements → layering → completion, ending on the
 * resolved space where the final statement holds and the CTA emerges. Fully
 * scroll-driven (no hijack) and reversible. Text is real HTML, left-aligned on
 * the gutter axis (mirrors the mobile app), over a light warm scrim.
 *
 * prefers-reduced-motion: reduce → a stacked, readable fallback (no pinning).
 */
export default function NarrativePause({ lines, testId = "narrative-pause" }) {
  const sectionRef = useRef(null);
  const imgRefs = useRef([]);
  const blockRefs = useRef([]); // lines 0–3
  const finalRef = useRef(null); // closing statement block
  const ctaRef = useRef(null); // CTA (inside final block)
  const [reduced, setReduced] = useState(false);

  const heads = lines.slice(0, 4);
  const closing = lines[4] || "";

  const handleExplore = (e) => {
    e.preventDefault();
    const el = document.getElementById("section-01");
    if (!el) return;
    const headerH = window.innerWidth >= 1024 ? 84 : 68;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setReduced(true); return undefined; }

    const apply = (y) => {
      const section = sectionRef.current;
      if (!section) return;
      const vhpx = window.innerHeight;
      const denom = section.offsetHeight - vhpx;
      const p = denom > 0 ? clamp01((y - section.offsetTop) / denom) : 0;
      const sp = p * 5; // narrative position 0..5 (6 states)

      // Images — image 0 is the base; each later image fades in over the one
      // before it (no mid-transition dimming). Image 4 holds through the end.
      imgRefs.current.forEach((el, k) => {
        if (!el) return;
        el.style.opacity = k === 0 ? "1" : String(smooth(clamp01(sp - (k - 1))));
      });

      // Lines 0–3 — crossfade bump, drifting subtly, in lock-step with images.
      for (let k = 0; k < 4; k += 1) {
        const el = blockRefs.current[k];
        if (!el) continue;
        const o = smooth(clamp01(1 - Math.abs(sp - k) * 1.5));
        el.style.opacity = String(o);
        const dy = Math.max(-10, Math.min(10, (sp - k) * -8));
        el.style.transform = `translateY(${dy}px)`;
      }

      // Closing statement — fades in over [3,4] with image 4, then holds.
      if (finalRef.current) {
        finalRef.current.style.opacity = String(smooth(clamp01(sp - 3)));
      }
      // CTA — emerges only after the statement settles, over [4,5].
      if (ctaRef.current) {
        const o = smooth(clamp01((sp - 4) * 1.3));
        ctaRef.current.style.opacity = String(o);
        ctaRef.current.style.pointerEvents = o > 0.5 ? "auto" : "none";
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        apply(window.scrollY || window.pageYOffset || 0);
        ticking = false;
      });
    };
    apply(window.scrollY || window.pageYOffset || 0);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [lines]);

  // Reduced-motion: readable, in-order, no pinning/crossfade.
  if (reduced) {
    return (
      <section data-testid={testId} className="bg-lux-ink">
        {IMAGES.map((src, i) => {
          const isFinal = i === IMAGES.length - 1;
          const text = i < 4 ? heads[i] : closing;
          return (
            <div key={src} className="relative min-h-[80vh] w-full overflow-hidden flex items-center">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${src})` }} />
              <div aria-hidden="true" className="absolute inset-0" style={{ background: LEFT_SCRIM }} />
              <div className="relative w-full max-w-[1200px] mx-auto px-6 md:px-12">
                <div className="max-w-[440px] md:max-w-[560px]">
                  <p
                    data-testid={`narrative-line-${i}`}
                    className={`font-serif italic font-light leading-[1.15] text-[26px] md:text-[40px] [text-shadow:0_2px_18px_rgba(0,0,0,0.55)] ${isFinal ? "text-lux-accent" : "text-white"}`}
                  >
                    {text}
                  </p>
                  {isFinal ? (
                    <div className="mt-9 md:mt-11">
                      <a
                        href="#section-01"
                        onClick={handleExplore}
                        data-testid="hero-explore-cta"
                        className="inline-flex items-center gap-2.5 pb-2 border-b border-white/55 text-white text-[12px] tracking-[0.22em] uppercase transition-colors hover:border-white"
                      >
                        Explore the Onedot6 experience <span>↓</span>
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </section>
    );
  }

  return (
    <section ref={sectionRef} data-testid={testId} className="relative bg-lux-ink" style={{ height: "600vh" }}>
      {/* Pinned cinematic stage */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Stacked crossfading images */}
        {IMAGES.map((src, k) => (
          <div
            key={src}
            ref={(el) => { imgRefs.current[k] = el; }}
            aria-hidden="true"
            className="absolute inset-0 bg-cover bg-center will-change-[opacity]"
            style={{ backgroundImage: `url(${src})`, opacity: k === 0 ? 1 : 0 }}
          />
        ))}
        {/* Warm left scrim for text legibility (images stay vivid) */}
        <div aria-hidden="true" className="absolute inset-0" style={{ background: LEFT_SCRIM }} />

        {/* Text stage — left-aligned on the gutter axis, vertically centred */}
        <div className="relative h-full w-full max-w-[1200px] mx-auto px-6 md:px-12 flex items-center pointer-events-none">
          <div className="relative w-full max-w-[440px] md:max-w-[560px] min-h-[260px]">
            {heads.map((line, i) => (
              <div
                key={line}
                ref={(el) => { blockRefs.current[i] = el; }}
                className="absolute inset-x-0 top-0"
                style={{ opacity: i === 0 ? 1 : 0, willChange: "opacity, transform" }}
              >
                <p
                  data-testid={`narrative-line-${i}`}
                  className="font-serif italic text-white font-light leading-[1.15] text-[26px] md:text-[40px] [text-shadow:0_2px_18px_rgba(0,0,0,0.55)]"
                >
                  {line}
                </p>
              </div>
            ))}

            {/* Closing statement + CTA (share the resolved final image) */}
            <div ref={finalRef} className="absolute inset-x-0 top-0" style={{ opacity: 0, willChange: "opacity" }}>
              <p
                data-testid="narrative-line-4"
                className="font-serif italic text-lux-accent font-light leading-[1.15] text-[28px] md:text-[44px] [text-shadow:0_2px_18px_rgba(0,0,0,0.55)]"
              >
                {closing}
              </p>
              <div ref={ctaRef} className="mt-9 md:mt-11" style={{ opacity: 0, pointerEvents: "none" }}>
                <a
                  href="#section-01"
                  onClick={handleExplore}
                  data-testid="hero-explore-cta"
                  className="inline-flex items-center gap-2.5 pb-2 border-b border-white/55 text-white text-[12px] tracking-[0.22em] uppercase transition-colors hover:border-white"
                >
                  Explore the Onedot6 experience <span>↓</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
